import * as React from 'react';
import { useRecoilState } from 'recoil';

import * as api from '@gitbook/api-client';
import { Icon } from '@gitbook/spine-icons';
import { SegmentedControl } from '@gitbook/spine-react';
import { useViewport } from '@gitbook/util-react-hooks';

import { LayoutSidebarContext } from '../../AppLayout';
import { PreviewDeviceFrame, SitePreviewIframe, sitePreviewAtom } from '../SitePreview';

/**
 * Props for the customization modal.
 */
export interface SiteCustomizationPreviewProps {
    site: api.Site;
    organization: api.Organization;
    previewSiteSpace: api.SiteSpace | undefined;
    settings: Partial<api.SiteCustomizationSettings>;
}

/**
 * Render the preview of the site with buttons to switch between desktop and mobile.
 */
export function SiteCustomizationPreview(props: SiteCustomizationPreviewProps): React.ReactElement {
    const { site, organization, previewSiteSpace, settings } = props;

    const [previewState, setPreviewState] = useRecoilState(sitePreviewAtom);
    const screenDimensions = useViewport();
    const { desktopSidebarWidth } = React.useContext(LayoutSidebarContext);

    return (
        <div className="flex h-full flex-1 flex-col items-center justify-between">
            <div className="mb-2 flex justify-center gap-2">
                <SegmentedControl<typeof previewState.theme>
                    active={previewState.theme}
                    options={[
                        {
                            value: api.CustomizationThemeMode.Light,
                            label: 'Light',
                            icon: Icon.ThemeLight,
                        },
                        {
                            value: api.CustomizationThemeMode.Dark,
                            label: 'Dark',
                            icon: Icon.ThemeDark,
                        },
                    ]}
                    onChange={(theme) =>
                        setPreviewState((prev) => ({
                            ...prev,
                            theme,
                        }))
                    }
                />
                <SegmentedControl<typeof previewState.device>
                    active={previewState.device}
                    options={[
                        {
                            value: 'desktop',
                            label: 'Desktop',
                            icon: Icon.DeviceDesktop,
                        },
                        {
                            value: 'mobile',
                            label: 'Mobile',
                            icon: Icon.DeviceMobile,
                        },
                    ]}
                    onChange={(device) =>
                        setPreviewState((prev) => ({
                            ...prev,
                            device,
                        }))
                    }
                />
            </div>

            <PreviewDeviceFrame
                frame={{
                    // Width of the screen, minus the width of the sidebar, the width of the customization panel and the padding
                    maxWidth: screenDimensions.width - desktopSidebarWidth - 512 - 64,
                    // Height of the screen, minus the height of the save footer, segmented control, padding, header, rounded app margin and border
                    maxHeight:
                        screenDimensions.height -
                        (30 + 16 * 2) -
                        (30 + 8) -
                        16 -
                        (screenDimensions.width < 1280 ? 97 : 49) -
                        13,
                }}
                device={previewState.device}
                src={previewSiteSpace?.urls.published ?? site.urls.published ?? ''}
                fixedHeight
            >
                <SitePreviewIframe
                    site={site}
                    content={{
                        siteSpaceId: previewSiteSpace?.id,
                    }}
                    organization={organization}
                    customization={settings}
                    className="size-full"
                    interactive
                />
            </PreviewDeviceFrame>
        </div>
    );
}
